import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { FileBadge, Plus, Trash2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg } from "@/lib/api";

const EMPTY = { name: "", category: "UTILITY", language: "it", body: "" };
const STATUS = { APPROVED: "bg-[#DCFCE7] text-[#15803D]", PENDING: "bg-orange-100 text-orange-700", REJECTED: "bg-red-100 text-red-700" };

export const TemplatesSection = () => {
  const qc = useQueryClient();
  const { data: items = [] } = useQuery({ queryKey: ["templates"], queryFn: () => api.get("/templates").then((r) => r.data) });
  const [f, setF] = useState(EMPTY);
  const [syncing, setSyncing] = useState(false);
  const refresh = () => qc.invalidateQueries({ queryKey: ["templates"] });

  const create = async (e) => {
    e.preventDefault();
    try { const { data } = await api.post("/templates", f); toast.success(data.source === "meta" ? "Modello inviato a Meta per approvazione" : "Modello demo creato"); setF(EMPTY); refresh(); }
    catch (err) { toast.error(errMsg(err)); }
  };
  const sync = async () => {
    setSyncing(true);
    try { const { data } = await api.post("/templates/sync"); toast.success(`${data.count} modelli sincronizzati da Meta`); refresh(); }
    catch (err) { toast.error(errMsg(err)); } finally { setSyncing(false); }
  };
  const remove = async (id) => { await api.delete(`/templates/${id}`); refresh(); toast.success("Modello eliminato"); };

  return (
    <section className="bg-white border border-slate-200 rounded-xl xl:col-span-2" data-testid="templates-section">
      <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-3">
        <span className="w-9 h-9 rounded-lg bg-slate-100 text-slate-700 grid place-items-center"><FileBadge size={17} /></span>
        <div><h3 className="font-semibold text-slate-900">Modelli di messaggio Meta</h3><p className="text-xs text-slate-500">Necessari per scrivere ai clienti oltre la finestra di 24 ore. Variabili: <code className="bg-slate-100 px-1 rounded">{"{{1}}"}</code>, <code className="bg-slate-100 px-1 rounded">{"{{2}}"}</code>…</p></div>
        <Button data-testid="templates-sync-button" variant="outline" size="sm" className="ml-auto" disabled={syncing} onClick={sync}><RefreshCw size={14} className={`mr-1.5 ${syncing ? "animate-spin" : ""}`} />Sincronizza da Meta</Button>
      </div>
      <div className="grid lg:grid-cols-[380px_1fr]">
        <form onSubmit={create} className="p-6 space-y-3 border-r border-slate-100">
          <Input data-testid="template-name-input" required placeholder="nome_modello" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="font-mono" />
          <div className="grid grid-cols-2 gap-3">
            <Select value={f.category} onValueChange={(v) => setF({ ...f, category: v })}>
              <SelectTrigger data-testid="template-category-select"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="UTILITY">Utility</SelectItem><SelectItem value="MARKETING">Marketing</SelectItem><SelectItem value="AUTHENTICATION">Autenticazione</SelectItem></SelectContent>
            </Select>
            <Input data-testid="template-language-input" required placeholder="it" value={f.language} onChange={(e) => setF({ ...f, language: e.target.value })} />
          </div>
          <Textarea data-testid="template-body-input" required rows={4} placeholder="Ciao {{1}}, il tuo ordine {{2}} è pronto." value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} />
          <Button data-testid="template-create-button" type="submit" className="w-full bg-[#0F172A] hover:bg-[#1E293B]"><Plus size={15} className="mr-1.5" />Crea modello</Button>
          <p className="text-[11px] text-slate-400">Con credenziali Meta e WABA ID il modello viene inviato a Meta per l'approvazione; altrimenti viene creato come modello demo.</p>
        </form>
        <ul className="divide-y divide-slate-100 max-h-[420px] overflow-y-auto thin-scroll">
          {items.map((t) => (
            <li key={t.id} data-testid={`template-item-${t.name}`} className="px-6 py-3.5 flex items-start gap-4 group">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <code className="text-sm font-semibold text-slate-800">{t.name}</code>
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${STATUS[t.status] || "bg-slate-100 text-slate-600"}`}>{t.status}</span>
                  <span className="text-[10px] uppercase tracking-wider text-slate-400">{t.category} · {t.language} · {t.source === "meta" ? "Meta" : "Demo"}</span>
                </div>
                <p className="text-sm text-slate-500 mt-1">{t.body}</p>
              </div>
              <Button data-testid={`template-delete-${t.name}`} variant="ghost" size="icon" className="opacity-60 group-hover:opacity-100" onClick={() => remove(t.id)}><Trash2 size={15} className="text-red-500" /></Button>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
};
